"use client";

import { useEffect, useState } from "react";

interface Heading {
 id: string;
 text: string;
 level: number;
}

interface TableOfContentsProps {
 contentSelector?: string;
}

export function TableOfContents({ contentSelector = ".prose" }: TableOfContentsProps) {
 const [headings, setHeadings] = useState<Heading[]>([]);
 const [activeId, setActiveId] = useState<string>("");

 useEffect(() => {
 const container = document.querySelector(contentSelector);
 if (!container) return;

 const elements = container.querySelectorAll("h2, h3");
 const items: Heading[] = [];
 elements.forEach((el) => {
 if (!el.id) {
 el.id = el.textContent?.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "";
 }
 items.push({
 id: el.id,
 text: el.textContent || "",
 level: parseInt(el.tagName[1]),
 });
 });
 setHeadings(items);
 }, [contentSelector]);

 useEffect(() => {
 if (headings.length === 0) return;

 const observer = new IntersectionObserver(
 (entries) => {
 entries.forEach((entry) => {
 if (entry.isIntersecting) {
 setActiveId(entry.target.id);
 }
 });
 },
 { rootMargin: "-80px 0px -80% 0px" }
 );

 headings.forEach(({ id }) => {
 const el = document.getElementById(id);
 if (el) observer.observe(el);
 });

 return () => observer.disconnect();
 }, [headings]);

 if (headings.length < 3) return null;

 return (
 <nav className="text-sm">
 <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">On This Page</h3>
 <ul className="space-y-1.5">
 {headings.map((heading) => (
 <li key={heading.id} style={{ paddingLeft: `${(heading.level - 2) * 12}px` }}>
 <a
 href={`#${heading.id}`}
 className={`block py-1 transition-colors ${
 activeId === heading.id
 ? "text-blue-600 font-medium"
 : "text-muted-foreground hover:text-foreground"
 }`}
 >
 {heading.text}
 </a>
 </li>
 ))}
 </ul>
 </nav>
 );
}
